/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import { dateStrToInt } from '@/helpers/dateTime';
import employee from '../../services/employee';
import notifications from '../../services/notifications';
import globalService from '../../services/global';

const normalizeModuleName = (value) => (value ? String(value).toLowerCase() : '');

const moduleMatches = (moduleItem, moduleName) => {
  const normalizedModuleName = normalizeModuleName(moduleName);
  return [moduleItem?.code, moduleItem?.module, moduleItem?.label, moduleItem?.displayName]
    .map(normalizeModuleName)
    .includes(normalizedModuleName);
};

export default {
  state: {
    // SHARED: Used by both rider and admin notifications
    activeNotification: null,
    isEditModeNotifications: false,
    selectedNotificationList: [],
    allNotifications: [],

    // ADMIN ONLY: Module list for categorization
    moduleList: [],

    // ADMIN ONLY: Default notification list for filtering
    defaultNotificationList: [],

    // ADMIN ONLY: Selected date type for date filter persistence
    selectedDateType: null,
    // ADMIN ONLY: Selected custom date range for display and persistence
    selectedDateRange: { fromDate: null, toDate: null },

    // ADMIN ONLY: Filter state for persistence across re-renders
    selectedModule: 'ALL',
    selectedRoute: null,
    selectedSearch: null,
  },

  mutations: {
    // SHARED: Used by both rider and admin
    SET_ACTIVE_NOTIFICATION(state, payload) {
      state.activeNotification = payload;
    },
    SET_IS_EDIT_MODE(state, payload) {
      state.selectedNotificationList = [];
      state.isEditModeNotifications = payload;
    },
    SET_NOTIFICATION_LIST(state, payload) {
      state.allNotifications = payload;
    },
    SET_SELECTED_NOTIFICATION_LIST(state, payload) {
      state.selectedNotificationList = payload;
    },

    // ADMIN ONLY: Module list and default notifications
    SET_MODULE_LIST(state, payload) {
      state.moduleList = payload;
    },
    SET_DEFAULT_NOTIFICATION_LIST(state, payload) {
      state.defaultNotificationList = payload;
    },
    SET_SELECTED_DATE_TYPE(state, payload) {
      state.selectedDateType = payload;
    },
    SET_SELECTED_DATE_RANGE(state, payload) {
      state.selectedDateRange = payload || { fromDate: null, toDate: null };
    },
    SET_SELECTED_MODULE(state, payload) {
      state.selectedModule = payload;
    },
    SET_SELECTED_ROUTE(state, payload) {
      state.selectedRoute = payload;
    },
    SET_SELECTED_SEARCH(state, payload) {
      state.selectedSearch = payload;
    },
  },

  actions: {
    // RIDER ONLY: Get rider notifications with read status update
    async getAllNotifications({ commit }, shouldUpdateReadStatus = true) {
      try {
        commit('SET_IS_LOADING', true, { root: true });
        const allNotifications = await employee.getNotifications({ shouldUpdateReadStatus });
        commit('SET_NOTIFICATION_LIST', allNotifications);
        if (allNotifications.length > 0) {
          commit('SET_ACTIVE_NOTIFICATION', allNotifications[0]);
        }
      } catch (error) {
        console.error(error);
      } finally {
        commit('SET_IS_LOADING', false, { root: true });
      }
    },

    // ADMIN ONLY: Fetch module list from API for notification categorization
    async fetchModuleList({ commit, rootState }) {
      try {
        const modules = await globalService.getCompanyModules();

        // Add displayName to each module (handle SHARED_RIDE custom name)
        const modulesWithDisplayName = modules.map((module) => {
          let displayName = module.label;

          if (module.code === 'SHARED_RIDE') {
            const sharedRideName = rootState.companySettings?.sharedRideModuleName;
            if (sharedRideName) {
              displayName = sharedRideName;
            }
          }

          return {
            ...module,
            displayName,
          };
        });

        commit('SET_MODULE_LIST', modulesWithDisplayName);
      } catch (error) {
        console.error('Error fetching module list:', error);
        // Fallback to empty array if API fails
        commit('SET_MODULE_LIST', []);
      }
    },

    // ADMIN ONLY: Get admin notifications with date filtering
    // Pass `_meta: { silent: true }` to skip toggling the global page loader
    // (use when refetching for filter changes to avoid a full-page overlay).
    async getAllNotificationsAdmin({ commit, getters }, payload = {}) {
      const { _meta = {}, ...apiPayload } = payload;
      const { silent = false } = _meta;
      try {
        if (!silent) commit('SET_IS_LOADING', true, { root: true });
        const allNotifications = await notifications.getNotifications(apiPayload);

        // Map module objects to each notification
        const notificationsWithModules = getters.notificationsWithModules(allNotifications);

        commit('SET_DEFAULT_NOTIFICATION_LIST', notificationsWithModules);
        commit('SET_NOTIFICATION_LIST', notificationsWithModules);
      } catch (error) {
        console.error(error);
        // Set lists to empty arrays to maintain consistent state on error
        commit('SET_DEFAULT_NOTIFICATION_LIST', []);
        commit('SET_NOTIFICATION_LIST', []);
        throw error;
      } finally {
        if (!silent) commit('SET_IS_LOADING', false, { root: true });
      }
    },

    // SHARED: Get notification details by ID
    async getNotificationDetails({ commit }, notificationId) {
      try {
        const details = await notifications.getNotificationDetails(notificationId);
        return details;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    // RIDER ONLY: Delete rider notifications (bulk or single)
    async deleteNotifications({ dispatch, state }, deleteActiveOnly = false) {
      if (state.selectedNotificationList.length === 0 && !deleteActiveOnly) return;
      try {
        const payload = deleteActiveOnly
          ? [state.activeNotification.id]
          : state.selectedNotificationList.map((item) => item.id);
        await employee.deleteNotifications(payload);
        if (!deleteActiveOnly) {
          state.selectedNotificationList = [];
        } else {
          state.activeNotification = null;
        }
        dispatch('getAllNotifications');
      } catch (error) {
        console.error(error);
      }
    },

    // ADMIN ONLY: Cancel/delete a single admin notification
    async deleteNotificationAdmin({ commit }, notificationId) {
      try {
        await notifications.cancelNotification(notificationId);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    toggleNotificationsEditMode({ commit, state }) {
      commit('SET_IS_EDIT_MODE', !state.isEditModeNotifications);
    },
    selectAllNotifications({ commit, state }) {
      commit('SET_SELECTED_NOTIFICATION_LIST', state.allNotifications);
    },
  },

  getters: {
    // SHARED: Used by both rider and admin
    allNotifications(state) {
      return state.allNotifications;
    },
    activeNotification(state) {
      return state.activeNotification;
    },
    isEditModeNotifications(state) {
      return state.isEditModeNotifications;
    },
    selectedNotificationList(state) {
      return state.selectedNotificationList;
    },

    // ADMIN ONLY: Module list
    moduleList(state) {
      return state.moduleList;
    },

    // ADMIN ONLY: Default notification list (unfiltered)
    defaultNotificationList(state) {
      return state.defaultNotificationList;
    },

    selectedDateType(state) {
      return state.selectedDateType;
    },

    selectedDateRange(state) {
      return state.selectedDateRange;
    },

    selectedModule(state) {
      return state.selectedModule;
    },
    selectedRoute(state) {
      return state.selectedRoute;
    },
    selectedSearch(state) {
      return state.selectedSearch;
    },

    // ADMIN ONLY: Map module objects to notifications
    notificationsWithModules: (state) => (notificationList) => {
      // Handle case where moduleList is empty or undefined (e.g., if fetchModuleList fails)
      const moduleList = Array.isArray(state.moduleList) ? state.moduleList : [];
      const defaultModule = { code: 'general', label: 'General', module: 'GENERAL' };

      return notificationList.map((notification) => {
        const moduleName = notification.moduleName || 'general';

        // Only attempt to find modules if moduleList is not empty
        if (moduleList.length === 0) {
          return {
            ...notification,
            module: defaultModule,
          };
        }

        // Match notification.moduleName against module.code (both lowercase)
        const moduleObj = moduleList.find((m) => moduleMatches(m, moduleName));
        const fallbackObj = moduleList.find((m) => moduleMatches(m, 'general'));

        return {
          ...notification,
          module: moduleObj || fallbackObj || defaultModule,
        };
      });
    },

    // ADMIN ONLY: Filter notifications by route and module
    filteredNotifications: (state) => (payload) =>
      state.defaultNotificationList.filter((item) => {
        const notificationRouteIds = [item.routeId, ...(Array.isArray(item.routeIds) ? item.routeIds : [])].filter(
          (routeId) => routeId !== null && routeId !== undefined,
        );
        const matchRoute =
          payload.routeId && Array.isArray(payload.routeId)
            ? notificationRouteIds.some((routeId) => payload.routeId.includes(routeId))
            : true;
        // Match against module.code (both lowercase)
        const matchModule =
          payload.moduleName && payload.moduleName !== 'ALL'
            ? moduleMatches(item.module, payload.moduleName) || moduleMatches(item, payload.moduleName)
            : true;

        return matchRoute && matchModule;
      }),
  },
};

/* eslint-enable no-param-reassign */
/* eslint-enable no-unused-vars */
